import { AppOrderSummary, SalesDetail } from "../../../../interfaces/sales";
import {
  getSellerNameWithAppOrderType,
  getTranslatedSaleStatus,
} from "../../../../translates/sales/sales";
import { formatCurrency } from "../../../../utils/moneyFormat";
import { formatDate, getLocalFromUtc } from "../../../../utils/dates";
import { getDistinctSecondarySeller } from "../../../../utils/getDistinctSecondarySeller";
import { isTipAdjustmentSale } from "../../../../utils/sales";
import { getTranslatedAppOrderStatus } from "../../../../translates/sales/sales";
import { logoImage } from "../../../../resources/logos/yoshibase64";
import { AppOrderField, PaymentField } from "../interfaces";
import { getAppOrderFields, getAppOrderStatusTone } from "./appOrderFields";
import {
  SalePaymentSection,
  formatPaymentFieldValue,
  getPaymentStatusLabel,
  getSalePaymentSections,
  getVisibleFields,
} from "./salePaymentFields";

/**
 * YOSHI-10444: pdfmake content for the sale detail export.
 *
 * Built to look like the screen instead of like a generic report: the same palette and type scale
 * Tailwind gives the detail (gray-500 labels over gray-700 values, gray-200 hairlines, gray-50
 * table headers, the brand ink on totals), the "Resumen" card split into the field grid and the
 * amounts panel, and one bordered card per payment with its status chip. Values are formatted with
 * the very helpers the screen uses, so paper and screen never disagree.
 */

/** Tailwind tokens the detail screen paints with, resolved to hex for pdfmake. */
const INK = "#111827"; // gray-900 — headings
const SUBHEAD = "#1f2937"; // gray-800 — section titles
const LABEL = "#6b7280"; // gray-500 — field labels
const VALUE = "#374151"; // gray-700 — field values
const MUTED = "#9ca3af"; // gray-400 — payment code
const BORDER = "#e5e7eb"; // gray-200 — hairlines and card borders
const HEADER_BG = "#f9fafb"; // gray-50 — table header band
const PRIMARY = "#d7015a"; // --color-primary-ink — totals

/** Width of the amounts panel of the "Resumen" card, in points. */
const AMOUNTS_WIDTH = 190;

/** Status chip palette, mirroring the badge classes of `SaleHeader` and `PaymentDetail`. */
const CHIP: Record<string, { fill: string; color: string }> = {
  red: { fill: "#fee2e2", color: "#991b1b" },
  blue: { fill: "#dbeafe", color: "#1e40af" },
  green: { fill: "#dcfce7", color: "#166534" },
  yellow: { fill: "#fef9c3", color: "#854d0e" },
  orange: { fill: "#ffedd5", color: "#9a3412" },
  gray: { fill: "#f3f4f6", color: "#1f2937" },
  amber: { fill: "#fef3c7", color: "#92400e" },
};

/** Sale status → chip tone, the same mapping the header badge uses. */
const SALE_STATUS_TONE: Record<number, string> = {
  0: "red",
  1: "blue",
  2: "green",
  3: "yellow",
  4: "red",
  5: "blue",
  6: "gray",
  7: "red",
};

/** Transaction status → chip tone, the same mapping `PaymentDetail` uses. */
const PAYMENT_STATUS_TONE: Record<number, string> = {
  0: "red",
  1: "green",
  2: "yellow",
  3: "orange",
  4: "blue",
  5: "gray",
  8: "yellow",
};

/** Card border with no inner rules: the frame around a card. */
const cardLayout = {
  hLineWidth: (i: number, node: any) =>
    i === 0 || i === node.table.body.length ? 0.5 : 0.5,
  vLineWidth: (i: number, node: any) =>
    i === 0 || i === node.table.widths.length ? 0.5 : 0,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingLeft: () => 10,
  paddingRight: () => 10,
  paddingTop: () => 8,
  paddingBottom: () => 8,
};

/** Invisible grid, for laying fields out inside a card. */
const bareLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 10,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/** A rounded chip is not available in pdfmake, so a filled one-cell table stands in. */
const chip = (text: string, tone: string): any => {
  const palette = CHIP[tone] || CHIP.gray;
  return {
    table: {
      body: [
        [
          {
            text,
            fontSize: 7,
            bold: true,
            color: palette.color,
            fillColor: palette.fill,
            margin: [4, 2, 4, 2],
          },
        ],
      ],
    },
    layout: bareLayout,
  };
};

/**
 * Runs of whitespace collapse to one space: several of the shared formatters embed double spaces
 * that HTML collapses on screen but a PDF would print verbatim.
 */
const clean = (value: string): string => value.replace(/\s+/g, " ").trim();

/**
 * Label over value, the pair the detail screen repeats everywhere. `note` adds the muted second
 * line the app order uses under the customer name.
 */
const field = (label: string, value: string, note?: string): any => ({
  stack: [
    { text: `${label}:`, fontSize: 7.5, color: LABEL, margin: [0, 0, 0, 2] },
    { text: clean(value) || "-", fontSize: 8.5, bold: true, color: VALUE },
    ...(note
      ? [{ text: clean(note), fontSize: 7.5, color: LABEL, margin: [0, 1, 0, 0] }]
      : []),
  ],
  margin: [0, 0, 0, 9],
});

/** Label over a status chip, for the cells that show a badge instead of plain text. */
const chipField = (label: string, text: string, tone: string): any => ({
  stack: [
    { text: `${label}:`, fontSize: 7.5, color: LABEL, margin: [0, 0, 0, 2] },
    { columns: [{ width: "auto", ...chip(text, tone) }, { width: "*", text: "" }] },
  ],
  margin: [0, 0, 0, 9],
});

/** Amount line of the amounts panel: label left, figure right. */
const amountLine = (
  label: string,
  value: string,
  options: { color?: string; size?: number } = {},
): any => ({
  columns: [
    {
      text: `${label}:`,
      fontSize: options.size || 8,
      color: options.color ? VALUE : LABEL,
      bold: !!options.color,
    },
    {
      text: value,
      fontSize: options.size || 8,
      bold: true,
      color: options.color || VALUE,
      alignment: "right",
    },
  ],
  margin: [0, 0, 0, 5],
});

/** Section heading, matching the `text-lg font-medium text-gray-800` headings on screen. */
const sectionTitle = (text: string): any => ({
  text,
  fontSize: 11,
  bold: true,
  color: SUBHEAD,
  margin: [0, 14, 0, 6],
});

/** Splits a field list into the two columns a payment card shows. */
const fieldColumn = (fields: PaymentField[]): any => ({
  stack: getVisibleFields(fields).map((item) =>
    field(item.label, formatPaymentFieldValue(item.value, item.type)),
  ),
});

/** The "Resumen" card: the field grid on the left, the amounts panel on the right. */
const buildSummaryCard = (sale: SalesDetail): any => {
  const primarySeller: string = getSellerNameWithAppOrderType(
    sale.seller?.name || "",
    sale.app_order_type,
  );
  const secondarySeller: string | undefined = getDistinctSecondarySeller(
    sale.seller?.name,
    sale.secondary_seller?.name,
  );
  const sellerLabel: string = secondarySeller
    ? `${primarySeller} · ${secondarySeller}`
    : primarySeller;

  const amounts: any[] = [
    amountLine("Subtotal", formatCurrency(sale.subtotal)),
    amountLine("Propina", formatCurrency(sale.tip)),
  ];
  // A reassigned tip is called out on screen; the note travels with the amounts.
  if (sale.reassigned_tip) {
    amounts.push({
      text: `Propina de ${formatCurrency(
        sale.reassigned_tip.amount,
      )} movida a la concesión “${sale.reassigned_tip.concession}”.`,
      fontSize: 6.5,
      color: "#b45309",
      margin: [0, 0, 0, 5],
    });
  }
  // The backend explains a tip that could not be reassigned; it is already a full sentence.
  if (sale.tip_reassignment_notice) {
    amounts.push({
      text: sale.tip_reassignment_notice,
      fontSize: 6.5,
      color: "#b45309",
      margin: [0, 0, 0, 5],
    });
  }
  // The commission only takes a line when the sale carries one, as on screen.
  if (sale.app_service_fee > 0) {
    amounts.push(
      amountLine("Comisión", formatCurrency(sale.app_service_fee)),
    );
  }
  // The rule above the total, standing in for the card's `border-t`.
  amounts.push({
    canvas: [
      {
        type: "line",
        x1: 0,
        y1: 0,
        x2: AMOUNTS_WIDTH - 20,
        y2: 0,
        lineWidth: 0.5,
        lineColor: BORDER,
      },
    ],
    margin: [0, 3, 0, 6],
  });
  amounts.push(
    // Same total the screen prints, in the same brand ink: the sale total excludes the commission.
    amountLine(
      "Total",
      formatCurrency(sale.total + (sale.app_service_fee ?? 0)),
      { color: PRIMARY, size: 9.5 },
    ),
  );

  return {
    table: {
      widths: ["*", AMOUNTS_WIDTH],
      body: [
        [
          {
            table: {
              widths: ["*", "*"],
              body: [
                [
                  field("Evento", sale.event?.name || "-"),
                  field("Concesión", sale.concession?.name || "-"),
                ],
                [
                  field("Vendedor", sellerLabel || "-"),
                  field(
                    "Fecha y hora",
                    sale.sold_at
                      ? `${formatDate(getLocalFromUtc(sale.sold_at))} hrs.`
                      : "-",
                  ),
                ],
              ],
            },
            layout: bareLayout,
          },
          { stack: amounts },
        ],
      ],
    },
    // The divider between the grid and the amounts, standing in for the card's `lg:border-l`.
    layout: {
      ...cardLayout,
      vLineWidth: () => 0.5,
    },
    unbreakable: true,
  };
};

/**
 * The "Pedido BM" card: customer and status first, then the delivery fields, laid out four per row
 * like the screen's `lg:grid-cols-4` grid. Fields come from the same `getAppOrderFields` the screen
 * renders, so the delivery target follows the same rules (pickup point, or section and seat).
 */
const buildAppOrderCard = (appOrder: AppOrderSummary): any => {
  const cells: any[] = [
    field(
      "Cliente",
      appOrder.customer_name || "-",
      appOrder.customer_phone_number || "-",
    ),
    chipField(
      "Estado",
      getTranslatedAppOrderStatus(appOrder.status),
      getAppOrderStatusTone(appOrder.status),
    ),
    ...getAppOrderFields(appOrder).map((item: AppOrderField) =>
      field(item.label, item.value),
    ),
  ];

  // Four per row, padding the last row so the grid keeps its column widths.
  const body: any[] = [];
  for (let index = 0; index < cells.length; index += 4) {
    const row: any[] = cells.slice(index, index + 4);
    while (row.length < 4) {
      row.push({ text: "" });
    }
    body.push(row);
  }

  return {
    table: { widths: ["*", "*", "*", "*"], body },
    layout: cardLayout,
    unbreakable: true,
  };
};

/** The "Productos" table, with the same columns and alignment as `productTableConfig`. */
const buildProductsTable = (sale: SalesDetail): any => {
  const header = (text: string, alignment: string): any => ({
    text,
    fontSize: 8,
    bold: true,
    color: LABEL,
    fillColor: HEADER_BG,
    alignment,
  });
  const cell = (text: string, alignment: string): any => ({
    text,
    fontSize: 8,
    color: VALUE,
    alignment,
  });

  const body: any[] = [
    [
      header("Producto", "left"),
      header("Cantidad", "center"),
      header("Tipo", "left"),
      header("Precio base", "right"),
      header("Precio de venta", "right"),
      header("Total", "right"),
    ],
  ];

  (sale.products || []).forEach((product) => {
    body.push([
      // Addons carry the same "—" prefix the on-screen table indents them with.
      cell(
        product.uuid_parent ? `—  ${product.product}` : product.product,
        "left",
      ),
      cell(product.quantity.toLocaleString("es-MX"), "center"),
      cell(product.discountName, "left"),
      cell(formatCurrency(product.base_price), "right"),
      cell(formatCurrency(product.price), "right"),
      cell(formatCurrency(product.subtotal), "right"),
    ]);
  });

  return {
    table: {
      // Repeated on every page, so a long sale stays readable.
      headerRows: 1,
      widths: ["*", 50, 90, 60, 68, 68],
      body,
    },
    // Row hairlines with no inner verticals, like the on-screen table.
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: (i: number, node: any) =>
        i === 0 || i === node.table.widths.length ? 0.5 : 0,
      hLineColor: () => BORDER,
      vLineColor: () => BORDER,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
  };
};

/** One payment card: the header band with its status chip and total, then the two field columns. */
const buildPaymentCard = (section: SalePaymentSection): any => ({
  table: {
    widths: ["*"],
    body: [
      [
        {
          columns: [
            {
              width: "auto",
              text: [
                { text: section.title, fontSize: 9, bold: true, color: SUBHEAD },
                { text: `  ${section.code}`, fontSize: 8, color: MUTED },
              ],
              margin: [0, 2, 0, 0],
            },
            {
              width: "auto",
              margin: [6, 0, 0, 0],
              ...chip(
                getPaymentStatusLabel(section.status),
                PAYMENT_STATUS_TONE[section.status] || "gray",
              ),
            },
            { width: "*", text: "" },
            {
              width: "auto",
              text: formatCurrency(section.total),
              fontSize: 9,
              bold: true,
              color: PRIMARY,
              alignment: "right",
              margin: [0, 2, 0, 0],
            },
          ],
        },
      ],
      [
        {
          table: {
            widths: ["*", "*"],
            body: [
              [fieldColumn(section.fields.left), fieldColumn(section.fields.right)],
            ],
          },
          layout: bareLayout,
        },
      ],
    ],
  },
  layout: cardLayout,
  unbreakable: true,
  margin: [0, 0, 0, 8],
});

/** Builds the whole document body for a sale. */
export const buildSalePdfContent = (sale: SalesDetail): any[] => {
  const content: any[] = [];

  // Header: title with its status chip on the left, brand mark on the right.
  content.push({
    columns: [
      {
        width: "auto",
        text: `Venta ${sale.code}`,
        fontSize: 17,
        bold: true,
        color: INK,
      },
      {
        width: "auto",
        margin: [8, 7, 0, 0],
        ...chip(
          getTranslatedSaleStatus(sale.status),
          SALE_STATUS_TONE[sale.status] || "gray",
        ),
      },
      // The screen shows this badge right after the status when the sale only moves a tip.
      ...(isTipAdjustmentSale(sale)
        ? [
            {
              width: "auto",
              margin: [6, 7, 0, 0],
              ...chip("Ajuste de propina", "amber"),
            },
          ]
        : []),
      { width: "*", text: "" },
      {
        image: logoImage,
        width: 66,
        height: 38,
      },
    ],
  });
  // The bill a sale belongs to, the link the header shows under the title.
  if (sale.bill) {
    content.push({
      text: `Cuenta ${sale.bill.name}`,
      fontSize: 8,
      bold: true,
      color: PRIMARY,
      margin: [0, 4, 0, 0],
    });
  }
  content.push({
    text: `Fecha de creación: ${formatDate(new Date().toISOString()).trim()} hrs.`,
    fontSize: 7,
    color: LABEL,
    margin: [0, 2, 0, 0],
  });

  content.push(sectionTitle("Resumen"));
  content.push(buildSummaryCard(sale));

  if (sale.app_order) {
    content.push(sectionTitle("Pedido BM"));
    content.push(buildAppOrderCard(sale.app_order));
  }

  // Comments travel with the sale, as they do on screen.
  if (sale.comments && sale.comments.length > 0) {
    content.push(
      sectionTitle(sale.comments.length === 1 ? "Comentario" : "Comentarios"),
    );
    content.push({
      table: {
        widths: ["*"],
        body: [
          [
            {
              stack: sale.comments.map((comment) => ({
                text: comment,
                fontSize: 8,
                color: VALUE,
              })),
            },
          ],
        ],
      },
      layout: cardLayout,
    });
  }

  content.push(sectionTitle("Productos"));
  content.push(buildProductsTable(sale));

  const paymentSections: SalePaymentSection[] = getSalePaymentSections(sale);
  if (paymentSections.length > 0) {
    content.push(sectionTitle("Pagos"));
    paymentSections.forEach((section) =>
      content.push(buildPaymentCard(section)),
    );
  }

  return content;
};
